import baseApi from 'services/api/base';
import getProductKey from 'helper/requests/getProductKey';
import { GetModelPerformanceMapDataParams } from 'types/Meteorology/Fields/ModelPerformance/getMapData';
import { ModelPerformanceMapData } from 'types/Meteorology/Fields/ModelPerformance/mapData';
import type {
  ListarTiposAnaliseResponse,
  ListarPeriodosResponse,
  ListarVariaveisResponse,
  ListarVariaveisParams,
  ListarRodadasResponse,
  ListarRodadasParams,
  ListarDatasValidadeResponse,
  ListarDatasValidadeParams,
  ModelPerformancePanelOption,
} from 'types/Meteorology/Fields/ModelPerformance/options';
import transformGetMapDataResponse from 'helper/Meteorology/Fields/ModelPerformance';

const basePath = 'produtos/meteorologia/mapas-meteogramas/desempenho-de-modelos';

function withProductKey(path: string): string {
  const productKey = getProductKey('meteorologia');
  return `${path}?product_key=${productKey}`;
}

type OptionItemRaw = { nome: string; label: string };
function optionItemToPanelOption(item: OptionItemRaw): ModelPerformancePanelOption {
  return { id: item.nome, label: item.label };
}

function tiposTransform(response: ListarTiposAnaliseResponse) {
  return (response.data?.data ?? []).map(optionItemToPanelOption);
}
function periodosTransform(response: ListarPeriodosResponse) {
  return (response.data?.data ?? []).map(optionItemToPanelOption);
}
function variaveisTransform(response: ListarVariaveisResponse) {
  return (response.data ?? []).map(optionItemToPanelOption);
}

export const modelPerformanceApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    listarTiposAnalise: build.query<ModelPerformancePanelOption[], void>({
      query: () => ({
        url: withProductKey(`${basePath}/listar-tipos-analise`),
        method: 'GET',
      }),
      transformResponse: tiposTransform,
    }),

    listarPeriodos: build.query<ModelPerformancePanelOption[], void>({
      query: () => ({
        url: withProductKey(`${basePath}/listar-periodos`),
        method: 'GET',
      }),
      transformResponse: periodosTransform,
    }),

    listarVariaveis: build.query<ModelPerformancePanelOption[], ListarVariaveisParams>({
      query: (body) => ({
        url: withProductKey(`${basePath}/listar-variaveis`),
        method: 'POST',
        body,
      }),
      transformResponse: variaveisTransform,
    }),

    listarRodadas: build.query<string[], ListarRodadasParams>({
      query: (body) => ({
        url: withProductKey(`${basePath}/listar-rodadas`),
        method: 'POST',
        body,
      }),
      transformResponse: (res: ListarRodadasResponse) => res.data ?? [],
    }),

    listarDatasValidadePrevisao: build.query<string[], ListarDatasValidadeParams>({
      query: (body) => ({
        url: withProductKey(`${basePath}/listar-datas-de-validade-previsao`),
        method: 'POST',
        body,
      }),
      transformResponse: (res: ListarDatasValidadeResponse) => res.data ?? [],
    }),

    getModelPerformanceMapData: build.query<
    ModelPerformanceMapData,
    GetModelPerformanceMapDataParams
    >({
      query: (params) => {
        const body: Record<string, unknown> = {
          tipo_analise: params.tipo_analise,
          variavel: params.variavel,
          periodo: params.periodo,
          datas_rodada: params.datas_rodada,
        };
        if (params.periodo === 'livre' && params.datas_validade?.length) {
          body.datas_validade = params.datas_validade;
        }
        return {
          url: withProductKey(`${basePath}/visao-geral/camadas-mapa`),
          method: 'POST',
          body,
        };
      },
      transformResponse: transformGetMapDataResponse,
      // Igual ao Windx: voltar a uma pêntada já consultada reexibe o mapa sem novo request.
      keepUnusedDataFor: 600,
    }),
  }),
});

export const {
  useListarTiposAnaliseQuery,
  useListarPeriodosQuery,
  useListarVariaveisQuery,
  useListarRodadasQuery,
  useListarDatasValidadePrevisaoQuery,
  useGetModelPerformanceMapDataQuery,
  useLazyGetModelPerformanceMapDataQuery,
} = modelPerformanceApi;
